import type { Edulead } from 'edulead';
import { eduleadV4 } from '../../services/edulead-v4-client';

/** The most ids one batch call takes; a longer paste goes in several calls. */
const BATCH_SIZE = 1000;

/** The ids of a paste: one per line or comma separated (semicolons and spaces too), each once. */
export function parseIds(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).filter((id) => id !== ''))];
}

function chunks<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Whether a batch adds the ids to the beacon or removes them from it. */
export type BeaconIdsVerb = 'Added' | 'Removed';

/** What the batches did: the institutions now in the beacon for the ids given (adds only), and
 * the ids, as given, that matched nothing. */
export interface BeaconIdsResult {
  items: Edulead.EduleadV4BeaconInstitution[];
  unmatchedIds: string[];
}

/** Adds or removes ids in batches; a batch that fails throws and stops the rest. */
export async function applyBeaconIds(
  beaconId: string,
  ids: string[],
  verb: BeaconIdsVerb,
): Promise<BeaconIdsResult> {
  const result: BeaconIdsResult = { items: [], unmatchedIds: [] };
  for (const batch of chunks(ids, BATCH_SIZE)) {
    if (verb === 'Added') {
      const res = await eduleadV4.beacons.batchCreateInstitutions({
        beacon_id: beaconId,
        ids: batch,
      });
      result.items.push(...(res.items ?? []));
      result.unmatchedIds.push(...(res.unmatched_ids ?? []));
    } else {
      const res = await eduleadV4.beacons.batchDeleteInstitutions({
        beacon_id: beaconId,
        ids: batch,
      });
      result.unmatchedIds.push(...(res.unmatched_ids ?? []));
    }
  }
  return result;
}
